import { call, put, select, takeLatest } from 'redux-saga/effects';
import { eachDayOfInterval, format } from 'date-fns';
import { AxiosResponse } from 'axios';

import { ActionType, ReprocessCreateRequestedAction } from './types';
import { SalesReprocessApi, AppStorage, FindReprocessResponse } from '../../services';
import {
  reprocessParametersSuccess,
  reprocessParametersFailure,
  reprocessFindFailure,
  reprocessFindSuccess,
  reprocessCreateFailure,
  reprocessCreateSuccess,
} from './actionCreators';
import { AppParameters, HttpRequestError, ReprocessFilter } from '../../types';
import {
  createReprocessRequestMap,
  createReprocessSalesPARequestMap,
  createReprocessEmiteMXRequestMap,
  findReprocessRequestMap,
  findReprocessResponseMap,
} from '../../helpers';
import { filterParametersDataSelector, reprocessFilterSelector } from '../selectors';

interface ExtendedAppParameters extends AppParameters {
  reprocess?: {
    statuses: string[];
    configuration?: {
      max_date_range: number;
    };
  };
}

function* getParameters() {
  try {
    const globalParameters: ExtendedAppParameters | undefined = yield select(
      filterParametersDataSelector
    );

    let reprocessParameters = AppStorage.getReprocessParameters();

    if (globalParameters && globalParameters.reprocess) {
      reprocessParameters = {
        statuses: globalParameters.reprocess.statuses,

        configuration: globalParameters.reprocess.configuration,
      };

      AppStorage.setReprocessParameters(reprocessParameters);
    }

    if (!reprocessParameters) {
      throw new Error('No se pudieron cargar los parámetros de reproceso (Global/Storage)');
    }

    const safePayload = reprocessParameters || { statuses: [] };

    yield put(reprocessParametersSuccess(safePayload));
  } catch (error) {
    yield put(reprocessParametersFailure(error as HttpRequestError));
  }
}

function* findReprocess() {
  const filters: ReprocessFilter = yield select(reprocessFilterSelector);

  try {
    const { data }: AxiosResponse<FindReprocessResponse> = yield call(
      SalesReprocessApi.findReprocess,
      findReprocessRequestMap(filters)
    );
    yield put(reprocessFindSuccess(findReprocessResponseMap(data)));
  } catch (error) {
    yield put(reprocessFindFailure(error as HttpRequestError));
  }
}

const PA_COUNTRY = 'PA';
const MX_COUNTRY = 'MX';
const EC_COUNTRY = 'EC';

function* createReprocess({ payload }: ReprocessCreateRequestedAction) {
  try {
    if (payload.country === PA_COUNTRY) {
      if (payload.store) {
        yield call(
          SalesReprocessApi.createReprocessSalesPA,
          createReprocessSalesPARequestMap(payload)
        );
      } else {
        const dateFrom = payload.businessDateFrom as Date;
        const dateTo = payload.businessDateTo as Date;
        const dates = eachDayOfInterval({ start: dateFrom, end: dateTo });
        // eslint-disable-next-line no-restricted-syntax
        for (const date of dates) {
          yield call(SalesReprocessApi.createReprocessFtp, {
            accountingDate: format(date, 'yyyy-MM-dd'),
          });
        }
      }
    } else if (payload.country === MX_COUNTRY) {
      yield call(
        SalesReprocessApi.createReprocessEmiteMX,
        createReprocessEmiteMXRequestMap(payload)
      );
    } else if (payload.country === EC_COUNTRY) {
      yield call(
        SalesReprocessApi.createReprocessSeedbilingEC,
        createReprocessSalesPARequestMap(payload)
      );
    } else {
      yield call(SalesReprocessApi.createReprocess, createReprocessRequestMap(payload));
    }
    yield put(reprocessCreateSuccess());
  } catch (error) {
    yield put(reprocessCreateFailure(error as HttpRequestError));
  }
}
/* eslint-enable no-restricted-syntax */

export default [
  takeLatest(ActionType.reprocessParametersRequested, getParameters),
  takeLatest(ActionType.reprocessFindRequested, findReprocess),
  takeLatest(ActionType.reprocessCreateRequested, createReprocess),
];
